// 📱 기기 및 상태 정보 섹션 컴포넌트
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import type { Application } from '../../../../types/application';
import type { CopyField } from '../types/applicationStickyHeader.types';

interface DeviceStatusSectionProps {
  application: Application;
  statusColor: string;
  deviceCount: number;
  copiedField: CopyField | null;
  getCopyIconStyles: (field: CopyField, size?: number) => object;
  getCopyTooltipTitle: (field: CopyField) => string;
  onCopy: (value: string, field: CopyField) => Promise<void>;
}

/**
 * 기기 및 상태 정보 섹션 컴포넌트
 * - 처리 상태 표시
 * - 신청 기기 수 표시
 * - 입금 계좌 정보 (있는 경우)
 */
export const DeviceStatusSection: React.FC<DeviceStatusSectionProps> = ({
  application,
  statusColor,
  deviceCount,
  copiedField,
  getCopyIconStyles,
  getCopyTooltipTitle,
  onCopy,
}) => {
  return (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'rgba(76, 175, 80, 0.04)',
          borderRadius: 2,
          border: '1px solid rgba(76, 175, 80, 0.1)',
          height: '100%',
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              처리 상태
            </Typography>
            <Chip
              label={application.status}
              sx={{
                bgcolor: statusColor,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.75rem',
                boxShadow: `0 2px 8px ${statusColor}40`,
              }}
            />
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              신청 기기
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {deviceCount}개
            </Typography>
          </Stack>

          {application.bank_name && application.bank_account && (
            <Stack spacing={0.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  입금 계좌
                </Typography>
                {/* 📋 계좌 복사 버튼 */}
                <Tooltip title={getCopyTooltipTitle('bank')}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const bankInfo = `${application.bank_name} ${application.bank_account} ${application.account_holder}`;
                      onCopy(bankInfo, 'bank');
                    }}
                    sx={getCopyIconStyles('bank', 24)}
                  >
                    {copiedField === 'bank' ? (
                      <CheckIcon sx={{ fontSize: 12 }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 12 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography variant="body2" fontWeight={500}>
                {application.bank_name} {application.bank_account}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {application.account_holder}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </Grid>
  );
};